import { Command, Slot } from './types';
import { buildSessionPatchFromUpdate, serializePresenterTime } from './stateAdapter';

const buildSlotRuntimePatches = (presenters: Presenter[]): Record<string, Partial<Slot>> => {
  const patches: Record<string, Partial<Slot>> = {};
  for (const p of presenters) {
    const patch: Partial<Slot> = {};
    if (p.startTime !== undefined && p.startTime !== '') {
      patch.startTime = serializePresenterTime(p.startTime);
    }
    if (p.endTime !== undefined && p.endTime !== '') {
      patch.endTime = serializePresenterTime(p.endTime);
    }
    if (p.dbElapsedTime !== undefined) {
      patch.dbElapsedTime = p.dbElapsedTime;
    }
    if (p.elapsedTime !== undefined) {
      patch.elapsedTime = p.elapsedTime;
    }
    if (Object.keys(patch).length > 0) {
      patches[p.id] = patch;
    }
  }
  return patches;
};

const bonusRuntimePatch = (bonus: Presenter): Partial<Slot> | undefined => {
  const patch: Partial<Slot> = {};
  if (bonus.startTime !== undefined && bonus.startTime !== '') {
    patch.startTime = serializePresenterTime(bonus.startTime);
  }
  if (bonus.endTime !== undefined && bonus.endTime !== '') {
    patch.endTime = serializePresenterTime(bonus.endTime);
  }
  if (bonus.dbElapsedTime !== undefined) {
    patch.dbElapsedTime = bonus.dbElapsedTime;
  }
  if (bonus.elapsedTime !== undefined) {
    patch.elapsedTime = bonus.elapsedTime;
  }
  if (bonus.currentDuration !== undefined) {
    patch.currentDuration = bonus.currentDuration;
  }
  return Object.keys(patch).length > 0 ? patch : undefined;
};

export const stateUpdateToCommands = (
  values: StateUpdateObject,
  ctx: {
    presenters: Presenter[];
    bonusTime: Presenter;
    cursor: number;
    meetingLength: number;
    presenterOrder: string[];
  }
): Command[] => {
  const commands: Command[] = [];

  if (values.presenterDelete?.length) {
    for (const p of values.presenterDelete) {
      commands.push({ type: 'REMOVE_SLOT', slotId: p.id });
    }
  }

  if (values.presenterAdd?.length) {
    for (const p of values.presenterAdd) {
      commands.push({
        type: 'ADD_SLOT',
        slot: {
          id: p.id,
          name: p.name,
          description: p.description,
          flight: p.flight,
          priority: p.priority,
          slotType: p.slotType,
          initialDuration: p.initialDuration,
          currentDuration: p.currentDuration,
          elapsedTime: p.elapsedTime,
          startTime: serializePresenterTime(p.startTime),
          endTime: serializePresenterTime(p.endTime),
          dbElapsedTime: p.dbElapsedTime,
        },
      });
    }
  }

  const patch = buildSessionPatchFromUpdate(values, ctx);

  if (values.cursor !== undefined) {
    const slotPatches =
      values.presentersUpdateAll !== undefined
        ? buildSlotRuntimePatches(values.presentersUpdateAll)
        : undefined;
    const bonusPatch = values.bonusTime ? bonusRuntimePatch(values.bonusTime) : undefined;
    commands.push({
      type: 'SET_CURSOR',
      cursor: values.cursor,
      slotPatches: slotPatches && Object.keys(slotPatches).length > 0 ? slotPatches : undefined,
      bonusPatch,
    });
    delete patch.cursor;
    if (values.presentersUpdateAll !== undefined) {
      delete patch.slots;
    }
    if (values.bonusTime !== undefined) {
      delete patch.bonus;
    }
  }

  if (Object.keys(patch).length > 0) {
    commands.push({ type: 'SYNC_STATE', state: patch });
  }

  return commands;
};
